'use client';
import React from 'react';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import Spinner from '@repo/ui/src/components/Spinner';
import type { useSearch } from '@hooks/search/useSearch';
import SearchResultCard from './_SearchResultCard';

interface Props {
  state: ReturnType<typeof useSearch>['data'];
  searchType: string;
  isLoading: boolean;
  isValidating: boolean;
  currentSize: number;
  resultTextSingular: string;
  resultTextPlural: string;
  onLoadMore: () => void;
}

const SearchGridView = ({
  state,
  searchType,
  isLoading,
  isValidating,
  currentSize,
  resultTextSingular,
  resultTextPlural,
  onLoadMore,
}: Props) => {
  const { t } = useTranslation();

  if ((isLoading || isValidating) && state.results.length === 0) {
    return (
      <div className="flex justify-center mt-10" data-testid="search-in-progress">
        <Spinner />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="mb-0 font-bold normal-case tra-text-web-body-l">
          {`${state.nbHits} ${state.nbHits === 1 ? resultTextSingular || 'Result' : resultTextPlural || 'Results'}`}
        </h2>
      </div>

      {state.results.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 xs:grid-cols-2 md:grid-cols-4 lg:grid-cols-5">
          {state.results.map((result: any, i: number) => (
            <div key={`${searchType}_${i}`} className="relative">
              <SearchResultCard result={result} searchType={searchType} />
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-8 text-center text-neutral-500">
          {t('search.noResults', undefined, { default: 'No results found.' })}
        </p>
      )}

      {isLoading && (
        <div className="flex justify-center mt-5 mb-3" data-testid="search-in-progress">
          <Spinner />
        </div>
      )}

      {!isLoading && currentSize < state.nbPages && (
        <div className="flex justify-center mt-6">
          <Button variant={ButtonVariant.tertiary} onClick={onLoadMore}>
            {t('gridContent.viewMore', undefined, { default: 'View More' })}
          </Button>
        </div>
      )}
    </div>
  );
};

export default SearchGridView;
